/**
 * PortraitFallback — used when a student's campaign has no photo.
 *
 * Renders a bold, editorial typographic tile instead of a broken image
 * or a generic silhouette: the student's first name is rendered giant
 * in italic serif on a solid palette color, with a small uppercase
 * eyebrow above and a school + class-year footer below. Doubles as a
 * privacy-preserving option for students who don't want their face
 * shared publicly.
 *
 * The palette rotates deterministically off the student's name so a
 * given student always gets the same color everywhere the card appears
 * (grid, detail hero, ending-soon rail, share previews).
 */

// Palette rotation — anchored to the site's editorial accents.
// Keyed by name so callers can either pin an intentional variant or
// let the deterministic hash pick.
const PALETTE = {
  cobalt: { bg: "bg-cobalt", fg: "text-sand", eyebrow: "text-sand/70" },
  tangerine: { bg: "bg-tangerine", fg: "text-ink", eyebrow: "text-ink/60" },
  grass: { bg: "bg-grass", fg: "text-sand", eyebrow: "text-sand/70" },
  blossom: { bg: "bg-blossom", fg: "text-ink", eyebrow: "text-ink/60" },
  ink: { bg: "bg-ink", fg: "text-sand", eyebrow: "text-sand/60" },
};
const PALETTE_KEYS = Object.keys(PALETTE);

const paletteFor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i += 1) {
    hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  }
  return PALETTE[PALETTE_KEYS[Math.abs(hash) % PALETTE_KEYS.length]];
};

const shortYear = (gradYear) => {
  if (!gradYear) return "";
  const y = String(gradYear).trim().slice(-2);
  return y ? `Class of \u2019${y}` : "";
};

/**
 * @param name       - "Maya R." full student name; first token is used
 * @param school     - "Stanford University" (rendered in footer eyebrow)
 * @param gradYear   - "2028" (rendered as "Class of '28")
 * @param variant    - optional palette key: "cobalt" | "tangerine" | "grass" | "blossom" | "ink"
 *                     (overrides the deterministic name-hash rotation)
 * @param className  - pass-through wrapper classes (aspect etc.)
 * @param nameSize   - Tailwind text-size for the giant name (default responsive clamp)
 */
export const PortraitFallback = ({
  name = "",
  school = "",
  gradYear = "",
  variant,
  className = "",
  nameSize = "text-[clamp(3rem,10vw,8rem)]",
}) => {
  const p = (variant && PALETTE[variant]) || paletteFor(name);
  const first = (name.split(" ")[0] || name).replace(/[.,]$/, "");

  return (
    <div
      className={`relative flex h-full w-full flex-col justify-between overflow-hidden ${p.bg} ${p.fg} ${className}`}
      data-testid="campaign-portrait-fallback"
    >
      <div className={`px-6 pt-5 text-[13px] font-semibold uppercase tracking-[0.22em] ${p.eyebrow} lg:px-8 lg:pt-6`}>
        Student
      </div>
      <div className="flex flex-1 items-center justify-center px-6 py-4 lg:px-8">
        <span className={`font-serif italic leading-[0.85] tracking-tight ${nameSize}`}>
          {first}
        </span>
      </div>
      <div className={`flex items-baseline justify-between gap-3 px-6 pb-5 text-[13px] font-semibold uppercase tracking-[0.18em] ${p.eyebrow} lg:px-8 lg:pb-6`}>
        <span className="min-w-0 flex-1 truncate">{school}</span>
        {gradYear && <span className="flex-shrink-0">{shortYear(gradYear)}</span>}
      </div>
    </div>
  );
};
